import { healingBowlPlayer, type HealingBowlSound } from "./healingBowlSounds";

export const PSYCHASSIST_SCORE_EVENT = "psychassist:score";
export const PSYCHASSIST_INFRACTION_EVENT = "psychassist:infraction";
export const PSYCHASSIST_INFRACTION_SOUND_EVENT =
  "psychassist:infraction-sound";
export const PSYCHASSIST_INFRACTION_SOUND_COOLDOWN_MS = 15_000;

type PsychAssistLevel = "unknown" | "steady" | "elevated" | "high" | "critical";

export interface PsychAssistSoundSignal {
  score?: number | null;
  erScore?: number | null;
  level?: PsychAssistLevel;
  source?: string;
  infractionCount?: number;
}

interface SoundBridgeOptions {
  cooldownMs?: number;
  now?: () => number;
  play?: (sound: HealingBowlSound) => Promise<void> | void;
  emitPlayed?: (detail: { sound: HealingBowlSound; level?: string }) => void;
}

export function shouldPlayPsychAssistInfractionSound(
  signal: PsychAssistSoundSignal,
): boolean {
  if (signal.level === "high" || signal.level === "critical") return true;
  const score = readScore(signal);
  return Number.isFinite(score) && score <= -5;
}

export function resolvePsychAssistInfractionSound(
  signal: PsychAssistSoundSignal,
): HealingBowlSound {
  const score = readScore(signal);
  if (signal.level === "critical" || (Number.isFinite(score) && score <= -7)) {
    return "shock-2";
  }
  return "shock-1";
}

export function installPsychAssistInfractionSoundBridge(
  options: SoundBridgeOptions = {},
) {
  if (typeof window === "undefined") return () => undefined;

  let lastPlayedAt = 0;
  const cooldownMs =
    options.cooldownMs ?? PSYCHASSIST_INFRACTION_SOUND_COOLDOWN_MS;
  const now = options.now ?? (() => Date.now());
  const play = options.play ?? ((sound) => healingBowlPlayer.play(sound));
  const emitPlayed =
    options.emitPlayed ??
    ((detail) => {
      window.dispatchEvent(
        new CustomEvent(PSYCHASSIST_INFRACTION_SOUND_EVENT, { detail }),
      );
    });

  const handler = (event: Event) => {
    const signal = readSignal(event);
    if (!shouldPlayPsychAssistInfractionSound(signal)) return;
    const nextNow = now();
    if (nextNow - lastPlayedAt < cooldownMs) return;
    lastPlayedAt = nextNow;
    const sound = resolvePsychAssistInfractionSound(signal);
    void play(sound);
    emitPlayed({ sound, level: signal.level });
  };

  window.addEventListener(PSYCHASSIST_SCORE_EVENT, handler);
  window.addEventListener(PSYCHASSIST_INFRACTION_EVENT, handler);

  return () => {
    window.removeEventListener(PSYCHASSIST_SCORE_EVENT, handler);
    window.removeEventListener(PSYCHASSIST_INFRACTION_EVENT, handler);
  };
}

function readSignal(event: Event): PsychAssistSoundSignal {
  const detail = (event as CustomEvent<unknown>).detail;
  if (!detail || typeof detail !== "object") return {};
  return detail as PsychAssistSoundSignal;
}

function readScore(signal: PsychAssistSoundSignal): number {
  const score = signal.score ?? signal.erScore;
  return typeof score === "number" ? score : Number.NaN;
}
